import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Check, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import { z } from "zod";
import { passwortFehlerText } from "@/lib/passwortFehler";
import { passwortMailFehler } from "@/lib/passwortVergessen";

/**
 * App-only Login Page (Apple Reader App compliant)
 * 
 * Identical to Login.tsx but WITHOUT:
 * - "Noch kein Konto? Jetzt registrieren" link
 * - "Zurück zur Startseite" link
 * 
 * Logo click navigates to /app instead of /
 * Password reset redirects back to /login-app
 */

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Bitte gib eine gültige E-Mail-Adresse ein" }),
  password: z.string().min(1, { message: "Bitte gib dein Passwort ein" }),
});

const passwordSchema = z.object({
  password: z.string()
    .min(8, { message: "Mindestens 8 Zeichen" })
    .regex(/[a-z]/, { message: "Mindestens ein Kleinbuchstabe" })
    .regex(/[A-Z]/, { message: "Mindestens ein Großbuchstabe" })
    .regex(/[0-9]/, { message: "Mindestens eine Zahl" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

const LoginApp = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Password setup state
  const [isPasswordRecoveryMode, setIsPasswordRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Forgot password state
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const checkUrlForRecovery = () => {
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    return hash.includes('type=recovery') || searchParams.get('type') === 'recovery';
  };

  const [isRecoveryFlowActive, setIsRecoveryFlowActive] = useState(checkUrlForRecovery);

  useEffect(() => {
    const checkAuth = async () => {
      const urlHasRecovery = checkUrlForRecovery();
      
      if (isRecoveryFlowActive || urlHasRecovery) {
        setIsRecoveryFlowActive(true);
        setIsPasswordRecoveryMode(true);
        setIsCheckingAuth(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/mein-bereich', { replace: true });
      }
      setIsCheckingAuth(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const urlHasRecovery = checkUrlForRecovery();
      
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryFlowActive(true);
        setIsPasswordRecoveryMode(true);
        setIsCheckingAuth(false);
        return;
      }
      
      if (event === 'SIGNED_IN' && session && !isRecoveryFlowActive && !isPasswordRecoveryMode && !urlHasRecovery) {
        setTimeout(async () => {
          const { data: { session: verifiedSession } } = await supabase.auth.getSession();
          if (verifiedSession) {
            navigate('/mein-bereich', { replace: true });
          }
        }, 300);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isPasswordRecoveryMode, isRecoveryFlowActive]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setIsLoading(true);
    setLoginError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setLoginError("no_account_or_wrong_password");
        } else if (error.message.includes("Email not confirmed")) {
          toast.error("E-Mail nicht bestätigt", { description: "Bitte bestätige zuerst deine E-Mail-Adresse." });
        } else {
          toast.error("Anmeldung fehlgeschlagen", { description: error.message });
        }
        return;
      }

      if (data.user) {
        toast.success("Erfolgreich angemeldet!");
      }
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", { description: "Bitte versuche es später erneut." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = passwordSchema.safeParse({ password: newPassword, confirmPassword });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setIsSettingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        toast.error("Fehler beim Speichern", { description: passwortFehlerText(error, error.message) });
        return;
      }

      toast.success("Passwort erfolgreich gespeichert!", {
        description: "Du wirst jetzt zu deinem Bereich weitergeleitet.",
      });

      setIsPasswordRecoveryMode(false);
      setTimeout(() => navigate('/mein-bereich'), 1000);
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", { description: "Bitte versuche es später erneut." });
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }

    const emailValidation = z.string().email().safeParse(email.trim());
    if (!emailValidation.success) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein");
      return;
    }

    setIsSendingResetEmail(true);

    try {
      const { error: fnError } = await supabase.functions.invoke('send-password-setup-email', {
        body: {
          email: email.trim(),
          redirect_to: `${window.location.origin}/login-app`,
          template_key: 'password_reset',
        },
      });

      if (fnError) {
        const hinweis = await passwortMailFehler(fnError);
        toast.error(hinweis.titel, { description: hinweis.text });
        return;
      }

      toast.success("E-Mail gesendet!", {
        description: "Schau in deinem Posteingang nach, auch im Spam-Ordner.",
      });
      setIsForgotPasswordMode(false);
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", { description: "Bitte versuche es später erneut." });
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Password recovery mode
  if (isPasswordRecoveryMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/app" className="inline-flex items-center justify-center">
              <img src={musikonzeptLogo} alt="MusiKonzept" className="h-16 w-auto" />
            </Link>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Passwort festlegen</CardTitle>
              <CardDescription>
                Lege dein persönliches Passwort fest, um auf deine Kurse zugreifen zu können.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      disabled={isSettingPassword}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  <div className="text-xs space-y-1 mt-2">
                    <div className={`flex items-center gap-1 ${newPassword.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {newPassword.length >= 8 ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens 8 Zeichen
                    </div>
                    <div className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[a-z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens ein Kleinbuchstabe
                    </div>
                    <div className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[A-Z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens ein Großbuchstabe
                    </div>
                    <div className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[0-9]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens eine Zahl
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSettingPassword}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-destructive">Passwörter stimmen nicht überein</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSettingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  size="lg"
                >
                  {isSettingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    "Passwort speichern"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot password mode
  if (isForgotPasswordMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/app" className="inline-flex items-center justify-center">
              <img src={musikonzeptLogo} alt="MusiKonzept" className="h-16 w-auto" />
            </Link>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Passwort vergessen?</CardTitle>
              <CardDescription>
                Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">E-Mail-Adresse</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="deine@email.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isSendingResetEmail}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSendingResetEmail || !email.trim()}
                  size="lg"
                >
                  {isSendingResetEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    "Reset-Link senden"
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 text-center text-sm">
              <button
                type="button"
                onClick={() => setIsForgotPasswordMode(false)}
                className="text-primary font-medium hover:underline"
              >
                ← Zurück zur Anmeldung
              </button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal login mode
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/app" className="inline-flex items-center justify-center">
            <img src={musikonzeptLogo} alt="MusiKonzept" className="h-16 w-auto" />
          </Link>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Willkommen zurück!</CardTitle>
            <CardDescription>
              Melde dich an, um auf deine Kurse zuzugreifen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginError === "no_account_or_wrong_password" && (
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p className="font-medium">E-Mail oder Passwort ist falsch.</p>
                  <p className="text-sm">
                    Du hast noch kein Konto? Falls du auf unserer alten Lernplattform warst, musst du dich hier neu registrieren – deine Kurse und Zertifikate werden automatisch übernommen.
                  </p>
                  <Link 
                    to="/registrieren" 
                    className="inline-block text-sm font-medium underline hover:no-underline mt-1"
                  >
                    → Jetzt kostenlos registrieren
                  </Link>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="deine@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Passwort</Label>
                  <button
                    type="button"
                    onClick={() => setIsForgotPasswordMode(true)}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Passwort vergessen?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  "Anmelden"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginApp;
